import { FilterView } from '@inspectify/design';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { reportActions, selectCurrentReport, selectReportOptions, } from '../redux/slices/reportSlice/report';
import { useAppDispatch, useAppSelector } from '../redux/store';
import CloseIconButton from './CloseIconButton';
import { Dictionary, ReportFilterEnum, ReportSystemGroupType } from '@inspectify/core';
import { useSearchParams } from 'react-router-dom';

const availableFilters = [
  {
    value: ReportFilterEnum.FullReport,
    filter: 'Full Report',
    helpText: 'See all data points captured on the inspection.',
  },
  { value: ReportFilterEnum.IssuesOnly, filter: 'Issues only', helpText: 'See only issues' },
  {
    value: ReportFilterEnum.MediaOnly,
    filter: 'Media only',
    helpText: 'See all attachments captured on the inspection.',
  },
  {
    value: ReportFilterEnum.AppliancesOnly,
    filter: 'Appliances only',
    helpText: 'See all Appliances captured on the inspection.',
  },
];

/**
 */
export const ReportFilterDialog: React.FC = () => {
  const { filters } = useAppSelector(selectReportOptions);
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = React.useState(false);
  const [selectedFilter, setSelectedFilter] = React.useState<string>(filters.items);
  const [selectedGroups, setSelectedGroups] = React.useState<Dictionary<ReportSystemGroupType>>(filters.groups);
  const [selectedFiltersLabel, setSelectedFiltersLabel] = useState('');
  const selectedReport = useAppSelector(selectCurrentReport);
  const reportSystemGroups = selectedReport.reportSystemGroups;

  const handleClose = () => setOpen(false);

  const handleOpen = () => setOpen(true);

  const handleSave = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    dispatch(reportActions.setFilters({
      items: selectedFilter,
      groups: selectedGroups,
    }));

    const groups = Object.values(selectedGroups);
    setSearchParams(prev => {
      prev.set('filters', selectedFilter);
      prev.set('units', groups.map(u => u.name!.toLowerCase()).join(','));
      return prev;
    })
    handleUpdateLabel();
    handleClose();
    setOpen(false);
  };

  const handleUnitChange = (unit: ReportSystemGroupType) => {
    if (selectedGroups[unit.id]?.id === unit.id) {
      setSelectedGroups((prev) => {
        const { [unit.id]: removed, ...rest } = prev;
        return rest;
      });
    } else {
      setSelectedGroups((prev) => ({ ...prev, [unit.id]: unit }));
    }
  }

  const handleUpdateLabel = () => {
    if (!selectedReport?.id) {
      setSelectedFiltersLabel('');
    }
    const selectedReportsLabel = selectedFilter === ReportFilterEnum.FullReport ? 'Full Report' : selectedFilter;
    const selectedUnitsLabel = Object.keys(selectedGroups).length ? ` - ${Object.values(selectedGroups).map(u => u.name)}` : '';
    setSelectedFiltersLabel(`${selectedReportsLabel}${selectedUnitsLabel}`);
  }

  useEffect(() => {
    if (reportSystemGroups.length) {
      const stringParam = searchParams.get('units');
      if (stringParam) {
        const unitsParam = stringParam.split(',');
        const selectedUnits: Dictionary<ReportSystemGroupType> = reportSystemGroups
          .filter(u => unitsParam.includes(u.name!.toLowerCase()))
          .reduce((acc, unit) => ({ ...acc, [unit.id]: unit }), {});

        if (selectedUnits) {
          setSelectedGroups(selectedUnits);
          dispatch(reportActions.setFilters({ groups: selectedUnits }));
        }
      }
    }
    const filterParams = searchParams.get('filters');
    if (filterParams) {
      setSelectedFilter(filterParams);

      dispatch(reportActions.setFilters({ items: filterParams }));
    }
    handleUpdateLabel();
  }, [reportSystemGroups, searchParams, dispatch]);

  if (!selectedReport?.id) return null;

  return (
    <Box>
      <Button fullWidth onClick={handleOpen} variant="outlined" startIcon={<FilterView />}>
        Filter: {selectedFiltersLabel}
      </Button>
      <Dialog
        onClose={handleClose} open={open}
        aria-labelledby="alert-dialog-title"
        slotProps={{
          paper: {
            component: 'form',
            onSubmit: handleSave,
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" variant="h3">
          Filters
        </DialogTitle>

        <CloseIconButton onClick={handleClose} />

        <DialogContent>
          {reportSystemGroups?.length > 0 && (
            <Stack spacing={2} direction="row" mb={2}>
              {reportSystemGroups.map(unit => (
                <FormControl key={unit.id}>
                  <Chip
                    label={unit.name}
                    aria-selected={selectedGroups[unit.id]?.id === unit.id}
                    sx={{ textTransform: 'capitalize' }}
                    color={selectedGroups[unit.id]?.id === unit.id ? 'primary' : 'default'}
                    variant={selectedGroups[unit.id]?.id === unit.id ? 'outlined' : 'filled'}
                    onClick={() => handleUnitChange(unit)}
                  />
                </FormControl>
              ))}
            </Stack>
          )}
          <FormControl>
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              defaultValue="Full Report"
              name="report-filters"
            >
              {availableFilters.map(({ filter, value, helpText }) => (
                <React.Fragment key={filter}>
                  <FormControlLabel
                    value={value}
                    control={<Radio />}
                    label={filter}
                    checked={filter === selectedFilter}
                    aria-describedby="my-helper-text"
                    onChange={() => setSelectedFilter(filter)}
                  />
                  <FormHelperText id="my-helper-text">
                    {helpText}
                  </FormHelperText>
                </React.Fragment>
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
